import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faShoppingCart,
  faTrash,
  faPlus,
  faMinus,
  faStore,
  faMapMarkerAlt,
  faTruck,
  faMoneyBillWave,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

// const DELIVERY_CHARGE = 49;

const HerCart = () => {
  const navigate = useNavigate();
  const { fetchCart, syncCart } = useHerKart();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadMyCart = async () => {
    setLoading(true);
    try {
      const response = await fetchCart();
      if (response?.success) {
        setCartItems(response.items || []);
      }
    } catch (e) {
      console.error("Cart fetch error:", e);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    loadMyCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync every change back to the server, but only after the initial load
  // completes — otherwise the empty initial state would overwrite real data.
  useEffect(() => {
    if (!hasLoaded) return;
    syncCart(cartItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cartItems]);

  const totalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartItems]);

  // const delivery = cartItems.length === 0 ? 0 : DELIVERY_CHARGE;
  const delivery = cartItems.reduce(
    (sum, item) => sum + (item.deliveryCharge || 0),
    0,
  );
  const discount = 0;
  const grandTotal = subtotal + delivery - discount;

  const increase = (productId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.productId === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item,
      ),
    );
  };

  const decrease = (productId) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const removeItem = (productId) => {
    setCartItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading your cart...</p>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#050816] text-white">
        <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4">
            <Link
              to="/herkart/products"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Shopping Cart</h1>

              <p className="text-xs text-slate-200">FoundHer Marketplace</p>
            </div>
          </div>
        </div>

        <div className="flex min-h-[75vh] flex-col items-center justify-center px-6 text-center">
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-white/5">
            <FontAwesomeIcon
              icon={faShoppingCart}
              className="text-5xl text-slate-500"
            />
          </div>

          <h2 className="mt-8 text-3xl text-cyan-100 font-bold">
            Your cart is empty
          </h2>

          <p className="mt-3 max-w-md text-slate-200">
            Browse women-led brands and add products to your shopping cart.
          </p>

          <Link
            to="/herkart/products"
            className="mt-8 rounded-2xl bg-pink-600 px-8 py-4 font-semibold transition hover:bg-pink-500"
          >
            Explore Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/products"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Shopping Cart</h1>

              <p className="text-xs text-slate-200">
                {totalItems} item{totalItems !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadMyCart}
              className="rounded-xl bg-white/10 w-fit px-2 py-1 text-xs hover:bg-white/20"
            >
              🔄 Refresh
            </button>

            <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
              <span className="font-semibold">₹{grandTotal}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {cartItems.map((item) => (
            <div
              key={item.productId}
              className="rounded-3xl border border-white/10 bg-white/5 p-4"
            >
              <div className="flex gap-4">
                <div className="flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500/20 to-violet-500/20 text-5xl">
                  🛍️
                </div>

                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <span className="rounded-full bg-pink-600/20 px-3 py-1 text-xs text-pink-300">
                      Women Entrepreneur
                    </span>

                    <h3 className="mt-2 text-lg text-cyan-50 font-bold">
                      {item.name}
                    </h3>
                    <p className="mt-1 text-sm text-slate-200">
                      by{" "}
                      <span className="text-pink-300">{item.entrepreneur}</span>
                    </p>
                    {item.remarks && (
                      <p className="mt-1 text-xs text-amber-300">
                        {item.remarks}
                      </p>
                    )}

                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-200">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faStore}
                          className="text-pink-400"
                        />
                        {item.store}
                      </div>

                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="text-pink-400"
                        />
                        {item.city}
                      </div>
                    </div>
                  </div>
                  {item.deliveryCharge && (
                    <div className="mt-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-2">
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={faTruck}
                          className="text-emerald-400"
                        />

                        <div>
                          <h4 className="font-semibold text-white">
                            Delivery Charges
                          </h4>

                          <p className="text-sm text-slate-200">
                            ₹{item.deliveryCharge}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {item.remarks && (
                    <div className="mt-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-2">
                      <div className="flex items-center gap-3">
                        <FontAwesomeIcon
                          icon={faTruck}
                          className="text-emerald-400"
                        />

                        <div>
                          <h4 className="font-semibold text-white">Remarks</h4>

                          <p className="text-sm text-slate-200">
                            {item.remarks}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                    <div className="text-2xl font-bold text-pink-400">
                      ₹{item.price}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => decrease(item.productId)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>

                      <span className="min-w-[40px] text-center text-lg font-bold">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => increase(item.productId)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-slate-200">Item Total</span>

                    <span className="text-xl font-bold">
                      ₹{item.price * item.quantity}
                    </span>
                  </div>

                  <button
                    onClick={() => removeItem(item.productId)}
                    className="mt-4 w-fit px-2 py-1 flex items-center gap-2 text-sm text-red-400 hover:text-red-300"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="sticky top-24 rounded-3xl border border-white/10 bg-white/5 p-6">
            <h2 className="text-xl text-cyan-100 font-bold">
              Shopping Summary
            </h2>

            <div className="mt-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-200">Items</span>
                <span>{totalItems}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-200">Subtotal</span>
                <span>₹{subtotal}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-200">Delivery Charges</span>
                <span>₹{delivery}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-200">Promotional Discount</span>
                <span>₹{discount}</span>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>

                  <span className="text-pink-400">₹{grandTotal}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  className="text-emerald-400"
                />

                <div>
                  <h4 className="font-semibold text-white">Cash on Delivery</h4>

                  <p className="text-sm text-slate-200">
                    Pay only when your order arrives.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faTruck} className="text-pink-400" />

                <div>
                  <h4 className="font-semibold text-white">
                    Estimated Delivery
                  </h4>

                  <p className="text-sm text-slate-200">
                    Instant in your Area/ Usually within 1-2 days in your city
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/herkart/checkout")}
              className="mt-4 flex w-fit px-2 py-1 items-center justify-center gap-3 rounded-2xl bg-pink-600 text-lg font-semibold transition hover:bg-pink-500"
            >
              Continue to Delivery Details
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

            <Link
              to="/herkart/products"
              className="mt-4 flex w-full items-center justify-center rounded-2xl border border-white/10 py-4 transition hover:bg-white/10"
            >
              Browse More Products
            </Link>
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm leading-6 text-gray-700 shadow-sm">
        <div className="mb-2 flex items-center gap-2 text-base font-semibold text-amber-800">
          ⚠️ Important Disclaimer
        </div>

        <p>
          <strong>HerKart</strong> provides a local order discovery platform
          only. The sale contract is directly between the{" "}
          <strong>customer</strong> and the <strong>merchant</strong>. HerKart
          does not participate in or control any transaction and is not
          responsible for payments, deliveries, product quality, refunds,
          warranties, or any disputes arising from a purchase.
        </p>

        <p className="mt-3">
          Each merchant is solely responsible for complying with all applicable
          laws, including GST, invoicing, taxation, licenses, permits, product
          regulations, and any other statutory or legal requirements. HerKart
          assumes no responsibility or liability for a merchant's legal, tax, or
          regulatory compliance.
        </p>
      </div>
    </div>
  );
};

export default HerCart;
